import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { COMMON_ERROR } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { addToast } from 'containers/Toasts/actions'

import { createIncidentErrorCodes } from '../ThreatLog/constants'
import { selectPageSize } from '../ThreatLog/selectors'

import {
  createAtoIncidentError,
  createAtoIncidentSuccess,
  fetchAccountInvestigationDataError,
  fetchAccountInvestigationDataSuccess,
  fetchDepartmentsError,
  fetchDepartmentsSuccess,
  getLicenseInformationError,
  getLicenseInformationSuccess,
} from './actions'
import {
  CREATE_ATO_INCIDENT_ASYNC,
  FETCH_DATA_ASYNC,
  FETCH_DEPARTMENTS_ASYNC,
  LICENSE_DATA_REQUEST_ASYNC,
  RESET_ALL_FILTERS,
  SET_ARRIVAL_DATE,
  SET_DEPARTMENT,
  SET_PAGE_NUMBER,
} from './constants'
import {
  selectAlertId,
  selectCompany,
  selectDateRange,
  selectEmailAddress,
  selectPageNumber,
  selectSelectedDepartment,
} from './selectors'

const fetchAccountInvestigationDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    // eslint-disable-next-line ironscales/fetch-selected-company-filter-guard -- the filter IS read, via selectCompany (./selectors), a createSelector over selectCompanyFilter the rule cannot follow across modules
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      FETCH_DATA_ASYNC.INITIATE,
      SET_PAGE_NUMBER,
      SET_ARRIVAL_DATE,
      SET_DEPARTMENT,
      RESET_ALL_FILTERS,
      CREATE_ATO_INCIDENT_ASYNC.SUCCESS
    ),
    filter(() => matchURL(URL_CONST.investigation.ato)),
    switchMap(() => {
      const state = state$.value
      return api.investigation
        .fetchAccountInvestigationData({
          company_ids: selectCompany(state),
          emailDateRange: selectDateRange(state),
          page_number: selectPageNumber(state),
          page_size: selectPageSize(state),
          email_address: selectEmailAddress(state),
          department: selectSelectedDepartment(state),
        })
        .pipe(
          map((response) => fetchAccountInvestigationDataSuccess(response.response)),
          catchError((error) => of(epicsErrorHandler(fetchAccountInvestigationDataError, error))),
          takeUntil(action$.pipe(ofType(FETCH_DATA_ASYNC.CANCEL)))
        )
    })
  )

const fetchAccountInvestigationDepartments = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_DEPARTMENTS_ASYNC.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.investigation.ato)
      return true
    }),
    mergeMap(() =>
      api.investigation
        .fetchAccountInvestigationDepartments(state$.value.partnerOptions.selectedCompany?.value)
        .pipe(
          map((response) => fetchDepartmentsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchDepartmentsError, error))),
          takeUntil(action$.pipe(ofType(FETCH_DEPARTMENTS_ASYNC.CANCEL)))
        )
    )
  )

const getLicenseInformationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      LICENSE_DATA_REQUEST_ASYNC.INITIATE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.investigation.ato)
      return true
    }),
    mergeMap(() =>
      api.dashboard.getLicenseInformation(state$.value.partnerOptions.selectedCompany?.value).pipe(
        map((response) => getLicenseInformationSuccess(response)),
        catchError((error) => of(epicsErrorHandler(getLicenseInformationError, error))),
        takeUntil(action$.pipe(ofType(LICENSE_DATA_REQUEST_ASYNC.CANCEL)))
      )
    )
  )

const createAccountInvestigationIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_ATO_INCIDENT_ASYNC.INITIATE),
    filter(() => matchURL(URL_CONST.investigation.ato)),
    switchMap(() =>
      api.investigation
        .createAccountInvestigationIncident(
          selectCompany(state$.value),
          selectAlertId(state$.value)
        )
        .pipe(
          switchMap((response) =>
            of(
              createAtoIncidentSuccess(response),
              addToast({
                id: 'Create_Ato_Incident_Toast_Message_Success',
                button: {
                  text: 'Go to incident',
                  id: 'Create_Ato_Incident_Toast_Message_Go_To_Incident_Button',
                  onClick: () => {
                    window.location = `${URL_CONST.incidents.attackdetails}/ato/${response?.response.incident_id}`
                  },
                },
                message: (
                  <span id="Create_Ato_Incident_Toast_Message">
                    Incident
                    <a
                      id="Create_Ato_Incident_Toast_Link"
                      className="white cp b"
                      href={`${URL_CONST.incidents.attackdetails}/ato/${response?.response.incident_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {` ATO-${response?.response.incident_id} `}
                    </a>
                    has been successfully created
                  </span>
                ),
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(
                () => createAtoIncidentError(error),
                error,
                createIncidentErrorCodes
              ),
              addToast({
                message: error.response?.error || error.response?.message || COMMON_ERROR,
                type: 'error',
                id: 'Create_ATO_Incident_Toast_Message_Error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(CREATE_ATO_INCIDENT_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  fetchAccountInvestigationDataEpic,
  fetchAccountInvestigationDepartments,
  getLicenseInformationEpic,
  createAccountInvestigationIncidentEpic
)
